import { useState } from 'react';
import { trpc } from '@/lib/trpc';
import { Spinner } from './EnvLayout';
import { ui, errText } from './envUi';

const PRESETS = [1, 5, 10, 15, 30, 60];

const fmt = (m: number) => {
  const rounded = Math.round(m * 100) / 100;
  if (rounded >= 60) { const h = Math.floor(rounded / 60), r = Math.round(rounded % 60); return r ? `${h} h ${r} min` : `${h} h`; }
  return `${rounded} min`;
};

/** How many real minutes make one day in the Plant game. */
export default function AdminPlant() {
  const utils = trpc.useUtils();
  const { data, isLoading, error } = trpc.environmental.plantSettings.useQuery();
  const save = trpc.environmental.setPlantMinutesPerDay.useMutation({
    onSuccess: () => { utils.environmental.plantSettings.invalidate(); setDraft(null); },
  });
  const [draft, setDraft] = useState<string | null>(null);

  if (isLoading) return <Spinner />;
  if (error || !data) return <p className="text-red-600 dark:text-red-400">{error ? errText(error) : 'Could not load the setting.'}</p>;

  const shown = draft ?? String(data.minutesPerDay);
  const value = Number(shown);
  const valid = shown.trim() !== '' && Number.isFinite(value) && value >= 0.1 && value <= 1440;
  const dirty = draft !== null && valid && value !== data.minutesPerDay;
  const preview = valid ? value : data.minutesPerDay;

  return (
    <section className={`${ui.card} p-5`}>
      <h2 className="mb-1 font-bold">Plant: length of a day</h2>
      <p className={`${ui.muted} mb-4`}>
        How many real-life minutes make one day in the Plant game. Players' gardens keep their progress; only the speed changes
        (the speed buttons inside the game still speed it up or pause it).
      </p>

      <div className="mb-3 flex flex-wrap items-center gap-3">
        <div className="flex items-center gap-2">
          <input type="number" min={0.1} max={1440} step="any" inputMode="decimal"
                 className={`${ui.input} !w-28`} value={shown} aria-label="Real minutes per game day"
                 onChange={e => setDraft(e.target.value)} />
          <span className={ui.muted}>minutes = 1 day</span>
        </div>
        <button className={ui.btn} disabled={!dirty || save.isPending} onClick={() => save.mutate({ minutes: value })}>
          {save.isPending ? 'Saving…' : 'Save'}
        </button>
        {save.isSuccess && draft === null && <span className="text-sm text-green-600 dark:text-green-400">Saved</span>}
      </div>

      <div className="mb-4 flex flex-wrap gap-2">
        {PRESETS.map(m => (
          <button key={m} className={ui.btnGhost} onClick={() => setDraft(String(m))}>{m} min</button>
        ))}
      </div>

      {!valid && <p className="mb-2 text-sm text-red-600 dark:text-red-400">Enter a number between 0.1 and 1440.</p>}
      {save.error && <p className="mb-2 text-sm text-red-600 dark:text-red-400">{errText(save.error)}</p>}

      <p className={ui.muted}>
        With {fmt(preview)} per day: one season (7 days) lasts {fmt(preview * 7)}, and a full year (4 seasons) lasts {fmt(preview * 28)}.
        Open Plant again after changing this to see the new speed.
      </p>
    </section>
  );
}
